import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Bell, Mail, Clock, Globe, CalendarDays, Send, Save } from "lucide-react";

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  v: h, label: `${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}`,
}));
const WEEKDAYS = [["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]];
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "America/Toronto",
  "Europe/London", "UTC",
];

const inputCls = "rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent bg-white";

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${checked ? "bg-emerald-500" : "bg-slate-200"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-6" : "left-0.5"}`} />
  </button>
);

const Row = ({ icon: Icon, title, desc, children }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-slate-100 last:border-0">
    <div className="flex items-start gap-3 min-w-0">
      <Icon className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium text-slate-900">{title}</p>
        {desc && <p className="text-sm text-slate-500">{desc}</p>}
      </div>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function Settings() {
  const { user, refresh } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");

  useEffect(() => {
    if (user) {
      setS({
        reminders_enabled: user.reminders_enabled !== false,
        reminder_hour: user.reminder_hour ?? 8,
        timezone: user.timezone || "America/New_York",
        digest_enabled: user.digest_enabled !== false,
        digest_weekday: user.digest_weekday ?? 0,
      });
    }
  }, [user]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/reminders/settings", s);
      await refresh();
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (kind) => {
    setTesting(kind);
    try {
      const r = await api.post(kind === "digest" ? "/reminders/test-digest" : "/reminders/test");
      toast.success(`Test ${kind === "digest" ? "digest" : "reminder"} sent to ${r.data?.to || user.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email");
    } finally {
      setTesting("");
    }
  };

  if (!s) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-slate-500 mt-1">Control when and how BidMatchAI emails you.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-emerald-900 text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h3 className="font-heading text-lg font-semibold mb-2 inline-flex items-center gap-2"><Bell className="h-5 w-5" /> Deadline reminders</h3>
        <Row icon={Mail} title="Email me about bid deadlines" desc="Sent 7, 3 & 1 day before each bid's deadline.">
          <Toggle checked={s.reminders_enabled} onChange={(v) => set("reminders_enabled", v)} testid="settings-reminders-toggle" />
        </Row>
        <Row icon={Clock} title="Send time" desc="The hour we email you, in your timezone.">
          <select className={inputCls} data-testid="settings-hour-select" value={s.reminder_hour} onChange={(e) => set("reminder_hour", +e.target.value)}>
            {HOURS.map((h) => <option key={h.v} value={h.v}>{h.label}</option>)}
          </select>
        </Row>
        <Row icon={Globe} title="Timezone">
          <select className={inputCls} data-testid="settings-timezone-select" value={s.timezone} onChange={(e) => set("timezone", e.target.value)}>
            {TIMEZONES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
        </Row>
        <div className="pt-4">
          <button onClick={() => sendTest("reminder")} disabled={testing === "reminder"} data-testid="settings-test-reminder-button" className="bg-white border border-slate-200 text-slate-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2">
            <Send className="h-4 w-4" /> {testing === "reminder" ? "Sending…" : "Send test reminder"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h3 className="font-heading text-lg font-semibold mb-2 inline-flex items-center gap-2"><CalendarDays className="h-5 w-5" /> Weekly pipeline digest</h3>
        <Row icon={Mail} title="Send me a weekly digest" desc="Readiness score, active bids, pipeline value & upcoming deadlines.">
          <Toggle checked={s.digest_enabled} onChange={(v) => set("digest_enabled", v)} testid="settings-digest-toggle" />
        </Row>
        <Row icon={CalendarDays} title="Digest day" desc="Sent at your chosen send time on this day.">
          <select className={inputCls} data-testid="settings-weekday-select" value={s.digest_weekday} onChange={(e) => set("digest_weekday", +e.target.value)}>
            {WEEKDAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <div className="pt-4">
          <button onClick={() => sendTest("digest")} disabled={testing === "digest"} data-testid="settings-test-digest-button" className="bg-white border border-slate-200 text-slate-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2">
            <Send className="h-4 w-4" /> {testing === "digest" ? "Sending…" : "Send test digest"}
          </button>
        </div>
      </div>
    </div>
  );
}
